import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { BluPopupNotification } from '.';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';
export type VariantType = 'filled' | 'outlined' | 'standard';

export interface PopupNotification {
  id: string;
  title?: string;
  message: string;
  type?: NotificationType;
  variant?: VariantType;
  duration?: number; // ms
  isCancellable?: boolean;
  handleCancel?: () => void;

  declineText?: string;
  handleDecline?: () => void;

  confirmText?: string;
  handleConfirm?: () => void;
}

interface PopupNotificationContextProps {
  notifications: PopupNotification[];
  showNotification: (notification: Omit<PopupNotification, 'id'>) => void;
  removeNotification: (id: string) => void;
}

const PopupNotificationContext = createContext<
  PopupNotificationContextProps | undefined
>(undefined);

export const useBluPopupNotification = () => {
  const context = useContext(PopupNotificationContext);
  if (!context) {
    throw new Error(
      'usePopupNotification must be used within a PopupNotificationProvider',
    );
  }
  return context;
};

export const BluPopupNotificationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [notifications, setNotifications] = useState<PopupNotification[]>([]);

  const showNotification = useCallback(
    (notification: Omit<PopupNotification, 'id'>) => {
      const id = Math.random().toString(36).substr(2, 9);
      setNotifications((prev) => [
        ...prev,
        {
          ...notification,
          id,
          type: notification.type ?? 'info',
          variant: notification.variant ?? 'filled',
          duration: notification.duration ?? 3000,
        },
      ]);
      if (notification.duration) {
        setTimeout(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        }, notification.duration);
      }
    },
    [],
  );

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <PopupNotificationContext.Provider
      value={{ notifications, showNotification, removeNotification }}
    >
      <BluPopupNotification />
      {children}
    </PopupNotificationContext.Provider>
  );
};
